<template>
  <teleport to="body">
    <!-- Scrim -->
    <transition name="xs-scrim">
      <div v-if="open" class="fixed inset-0 z-[409] bg-black/40 backdrop-blur-[1px]" @click="close"></div>
    </transition>

    <!-- End-side slide-over (start-side under RTL) -->
    <transition :name="dir === 'rtl' ? 'xs-drawer-rtl' : 'xs-drawer-ltr'">
      <aside v-if="open"
        class="fixed inset-y-0 end-0 z-[410] w-full sm:w-[400px] max-w-full bg-white shadow-2xl flex flex-col"
        :dir="dir"
        role="dialog"
        :aria-label="$t('cart_title')">
        <!-- Header -->
        <div class="flex items-center justify-between gap-3 px-4 h-16 border-b border-[#e2e8f0] flex-shrink-0">
          <h2 class="text-base font-semibold text-[#2d3748] flex items-center gap-2">
            <ShoppingCart class="w-5 h-5 text-[var(--color-primary)]" />
            {{ $t('cart_title') }}
            <span v-if="items.length" class="text-sm font-normal text-[#64748b]">({{ items.length }})</span>
          </h2>
          <div class="flex items-center gap-1">
            <button v-if="items.length" type="button" @click="clearCart"
              class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#64748b] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] rounded-lg transition-colors">
              <Trash2 class="w-4 h-4" />{{ $t('cart_clear') }}
            </button>
            <button type="button" @click="close" :aria-label="$t('cart_title')"
              class="w-9 h-9 flex items-center justify-center rounded-lg text-[#64748b] hover:bg-[#f1f5f9] transition-colors">
              <X class="w-5 h-5" />
            </button>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="!items.length" class="flex-1 flex flex-col items-center justify-center text-center px-8 gap-3">
          <div class="w-16 h-16 rounded-2xl bg-[var(--color-primary-light)] flex items-center justify-center text-[var(--color-primary)]">
            <ShoppingCart class="w-8 h-8" />
          </div>
          <p class="text-[#2d3748] font-medium">{{ $t('cart_empty') }}</p>
          <p class="text-sm text-[#64748b] max-w-[24ch]">{{ $t('cart_empty_hint') }}</p>
        </div>

        <!-- Items -->
        <div v-else class="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          <div v-for="item in items" :key="item.orderNo"
            class="rounded-2xl border border-[#e2e8f0] overflow-hidden">
            <div class="flex gap-3 p-3">
              <!-- Thumbnail -->
              <div class="w-16 h-16 flex-shrink-0 rounded-xl bg-[#f7fafc] overflow-hidden flex items-center justify-center">
                <img :src="item.thumbnail" :alt="fittingLabel(item) || productName(item.productId)"
                  class="w-full h-full object-contain"
                  loading="lazy" decoding="async"
                  @error="onThumbError($event, item)" />
              </div>

              <div class="min-w-0 flex-1">
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <p class="text-sm font-semibold text-[#2d3748] leading-tight truncate">{{ productName(item.productId) }}</p>
                    <p class="text-xs text-[#64748b] truncate">
                      {{ item.kind === 'diy' ? diyLabel(item) : (fittingLabel(item) || $t('cart_whole_line')) }}
                    </p>
                  </div>
                  <button type="button" @click="removeItem(item.orderNo)" :aria-label="$t('cart_remove')"
                    class="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-[#94a3b8] hover:text-red-500 hover:bg-red-50 transition-colors">
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>

                <!-- Chips -->
                <div class="flex flex-wrap items-center gap-1.5 mt-1.5">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                    {{ item.kind === 'diy' ? $t('cart_kind_diy') : $t('cart_kind_catalog') }}
                  </span>
                  <span v-if="item.size" class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#f1f5f9] text-[#475569]">
                    {{ $t('cart_size') }}: {{ item.size }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Order no + copy -->
            <div class="flex items-center justify-between gap-2 px-3 py-2 bg-[#f8fafc] border-t border-[#e2e8f0]">
              <span class="text-[11px] text-[#64748b] flex-shrink-0">{{ $t('cart_order_no') }}</span>
              <div class="flex items-center gap-1.5 min-w-0">
                <code class="text-[11px] font-mono text-[#2d3748] truncate">{{ item.orderNo }}</code>
                <button type="button" @click="copyOrder(item.orderNo)"
                  :aria-label="$t('cart_copy_order')"
                  class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-[#94a3b8] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors">
                  <component :is="copiedNo === item.orderNo ? Check : Copy" class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <!-- Editable qty + note -->
            <div class="px-3 py-2.5 border-t border-[#e2e8f0] space-y-2">
              <div class="flex items-center gap-2">
                <label class="text-[11px] font-medium text-[#4a5568] flex-shrink-0">{{ $t('cart_qty') }}</label>
                <input type="number" min="1" step="1" :value="item.qty || 1"
                  @change="setQty(item, $event)"
                  class="w-20 xs-mini-input" />
              </div>
              <input type="text" :value="item.note"
                @change="updateItem(item.orderNo, { note: $event.target.value })"
                :placeholder="$t('cart_note_ph')"
                class="w-full xs-mini-input" />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div v-if="items.length" class="flex-shrink-0 border-t border-[#e2e8f0] p-4 space-y-2">
          <button type="button" @click="requestQuote"
            class="w-full py-2.5 rounded-lg bg-[var(--color-primary)] text-white text-sm font-semibold hover:bg-[var(--color-primary-dark)] transition-colors flex items-center justify-center gap-2">
            <FileText class="w-4 h-4" />{{ $t('cart_request_quote') }}
          </button>
          <button type="button" @click="askAi"
            class="w-full py-2.5 rounded-lg border border-[#e2e8f0] text-[#4a5568] text-sm font-medium hover:bg-[#f7fafc] hover:text-[var(--color-primary)] transition-colors flex items-center justify-center gap-2">
            <MessageCircle class="w-4 h-4" />{{ $t('cart_ask_ai') }}
          </button>
        </div>
      </aside>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ShoppingCart, X, Trash2, Copy, Check, FileText, MessageCircle } from 'lucide-vue-next'
import { productsData } from '../data/products'
import { getFittingName } from '../i18n/fittingNames'
import { cartItems, updateItem, removeItem, clearCart } from '../services/cart'

const router = useRouter()
const { t, locale } = useI18n()

const open = ref(false)
const copiedNo = ref('')
let copyTimer = null

const items = computed(() => cartItems.value)
const dir = computed(() => (locale.value === 'ar' ? 'rtl' : 'ltr'))

function productName(productId) {
  const p = productsData.find(x => x.id === productId)
  return p ? (p.name[locale.value] || p.name.en) : productId
}
function fittingLabel(item) {
  return item.fittingKey ? getFittingName(item.fittingKey, locale.value) : ''
}
function diyLabel(item) {
  const d = item.diy || {}
  const parts = [d.fittingType, Array.isArray(d.sizes) && d.sizes.length ? d.sizes.join(', ') : null].filter(Boolean)
  return parts.length ? parts.join(' · ') : t('cart_kind_diy')
}

function onThumbError(e, item) {
  const el = e.target
  const fallback = `/images/categories/${item.productId}.jpg`
  if (el.dataset.fb !== '1' && el.getAttribute('src') !== fallback) {
    el.dataset.fb = '1'
    el.src = fallback
  } else {
    el.style.display = 'none'
  }
}

function setQty(item, e) {
  const n = Math.max(1, Math.floor(Number(e.target.value) || 1))
  e.target.value = n
  updateItem(item.orderNo, { qty: n })
}

async function copyOrder(orderNo) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(orderNo)
    }
    copiedNo.value = orderNo
    clearTimeout(copyTimer)
    copyTimer = setTimeout(() => { copiedNo.value = '' }, 1500)
  } catch (_) { /* clipboard denied — no-op */ }
}

function requestQuote() {
  close()
  router.push({ path: '/quote', query: { cart: 'all' } })
}

function askAi() {
  close()
  window.dispatchEvent(new CustomEvent('xs-open-chat-cart'))
}

function openDrawer() { open.value = true }
function close() { open.value = false }

onMounted(() => window.addEventListener('xs-open-cart', openDrawer))
onUnmounted(() => { window.removeEventListener('xs-open-cart', openDrawer); clearTimeout(copyTimer) })
</script>

<style scoped>
.xs-mini-input {
  padding: 0.35rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.78rem;
  color: #2d3748;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.xs-mini-input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-a10);
}
.xs-scrim-enter-active, .xs-scrim-leave-active { transition: opacity 0.25s ease; }
.xs-scrim-enter-from, .xs-scrim-leave-to { opacity: 0; }
.xs-drawer-ltr-enter-active, .xs-drawer-ltr-leave-active,
.xs-drawer-rtl-enter-active, .xs-drawer-rtl-leave-active { transition: transform 0.3s ease; }
.xs-drawer-ltr-enter-from, .xs-drawer-ltr-leave-to { transform: translateX(100%); }
.xs-drawer-rtl-enter-from, .xs-drawer-rtl-leave-to { transform: translateX(-100%); }
</style>
